/**
 * CLI Account Board Management
 *
 * Superadmin CRUD for the Accounts board (cards.boardType='account') and the
 * account↔customer-org link, scoped to the org the CLI user is logged into.
 * Backed by `/superadmin/account-board/*`.
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, fetchList, getTargetOrg } from './cli-helpers.js';

type LinkedOrgRef = { id: string; name: string };

type Account = {
  id: string;
  title: string;
  description: string;
  stage: string;
  sortOrder: number;
  linkedOrg: LinkedOrgRef | null;
};

type AccountApiResponse = {
  ok: boolean;
  error?: string;
  account?: Account;
  linkedOrg?: LinkedOrgRef | null;
};

const STAGES = ['prospect', 'qualified', 'engaged', 'trial', 'active', 'churned', 'disqualified'];

function formatAccount(account: Account): string {
  const linked = account.linkedOrg ? ` org=${account.linkedOrg.name}` : '';
  return `  ${account.id}  ${account.title}  (stage=${account.stage})${linked}`;
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const params = new URLSearchParams();
  const search = getArgValue(argv, '--search');
  if (search) params.set('search', search);
  const stage = getArgValue(argv, '--stage');
  if (stage) params.set('stage', stage);
  const query = params.toString();
  const path = `/superadmin/account-board/accounts${query ? `?${query}` : ''}`;
  const accounts = await fetchList<Account>(apiUrl, token, path, 'accounts');

  if (jsonOutput) {
    console.log(JSON.stringify(accounts, null, 2));
    return;
  }
  if (accounts.length === 0) {
    console.log('No accounts found.');
    return;
  }
  for (const account of accounts) console.log(formatAccount(account));
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const title = argv[0];
  if (!title || title.startsWith('--')) {
    console.error('Error: Missing account name.');
    console.error('Usage: canary account create <name> [--description <text>] [--stage <stage>]');
    process.exit(1);
  }

  const description = getArgValue(argv, '--description');
  const stage = getArgValue(argv, '--stage');

  const result = await apiRequest<AccountApiResponse>(
    apiUrl,
    token,
    'POST',
    '/superadmin/account-board/accounts',
    {
      title,
      ...(description !== undefined ? { description } : {}),
      ...(stage !== undefined ? { stage } : {}),
    }
  );

  if (!result.ok || !result.account) {
    console.error(`Error: ${result.error ?? 'Failed to create account'}`);
    process.exit(1);
  }
  console.log(`Created account ${result.account.id}: ${result.account.title} (stage=${result.account.stage})`);
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing account id.');
    console.error('Usage: canary account set <id> [--title <text>] [--description <text>] [--stage <stage>]');
    process.exit(1);
  }

  const title = getArgValue(argv, '--title');
  const description = getArgValue(argv, '--description');
  const stage = getArgValue(argv, '--stage');

  if (title === undefined && description === undefined && stage === undefined) {
    console.error('Error: Provide at least one of --title, --description, --stage.');
    process.exit(1);
  }

  const result = await apiRequest<AccountApiResponse>(
    apiUrl,
    token,
    'PATCH',
    `/superadmin/account-board/accounts/${encodeURIComponent(id)}`,
    {
      ...(title !== undefined ? { title } : {}),
      ...(description !== undefined ? { description } : {}),
      ...(stage !== undefined ? { stage } : {}),
    }
  );

  if (!result.ok || !result.account) {
    console.error(`Error: ${result.error ?? 'Failed to update account'}`);
    process.exit(1);
  }
  console.log(`Updated account ${result.account.id}: ${result.account.title}`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing account id.');
    console.error('Usage: canary account delete <id>');
    process.exit(1);
  }

  const result = await apiRequest<AccountApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `/superadmin/account-board/accounts/${encodeURIComponent(id)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`Deleted account ${id}`);
}

async function handleLinkOrg(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  const orgId = getTargetOrg(argv);
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing account id.');
    console.error('Usage: canary account link-org <id> --target-org <customerOrgId>');
    process.exit(1);
  }
  if (!orgId) {
    console.error('Error: --target-org <customerOrgId> is required.');
    process.exit(1);
  }

  const result = await apiRequest<AccountApiResponse>(
    apiUrl,
    token,
    'PUT',
    `/superadmin/account-board/accounts/${encodeURIComponent(id)}/linked-org`,
    { customerOrgId: orgId }
  );

  if (!result.ok || !result.linkedOrg) {
    console.error(`Error: ${result.error ?? 'Failed to link org'}`);
    process.exit(1);
  }
  console.log(`Linked account ${id} to org ${result.linkedOrg.name} (${result.linkedOrg.id})`);
}

async function handleUnlinkOrg(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing account id.');
    console.error('Usage: canary account unlink-org <id>');
    process.exit(1);
  }

  const result = await apiRequest<AccountApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `/superadmin/account-board/accounts/${encodeURIComponent(id)}/linked-org`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`Unlinked org from account ${id}`);
}

async function handleLinkedOrg(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  const jsonOutput = hasFlag(argv, '--json');
  if (!id || id.startsWith('--')) {
    console.error('Usage: canary account linked-org <id>');
    process.exit(1);
  }

  const result = await apiRequest<AccountApiResponse>(
    apiUrl,
    token,
    'GET',
    `/superadmin/account-board/accounts/${encodeURIComponent(id)}/linked-org`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  if (jsonOutput) {
    console.log(JSON.stringify(result.linkedOrg ?? null, null, 2));
    return;
  }
  console.log(
    result.linkedOrg
      ? `Account ${id} linked to org ${result.linkedOrg.name} (${result.linkedOrg.id})`
      : `Account ${id} has no linked org.`
  );
}

function printAccountHelp(): void {
  console.log(
    [
      'Usage: canary account <sub-command> [options]',
      '',
      'Manage the Accounts board (cards.boardType=account) for the org you are logged into.',
      '',
      'Sub-commands:',
      '  list [--search <text>] [--stage <stage>] [--json]   List accounts',
      '  create <name> [--description <text>] [--stage <stage>]',
      '                                                Create an account (defaults to stage=prospect)',
      '  set <id> [--title <text>] [--description <text>] [--stage <stage>]',
      '                                                Update an account\'s fields',
      '  delete <id>                                   Delete an account',
      '  link-org <id> --target-org <customerOrgId>    Bind the account to a customer organization',
      '  unlink-org <id>                               Remove the account\'s org link',
      '  linked-org <id> [--json]                      Show the account\'s linked org',
      '',
      `Stages: ${STAGES.join(', ')}`,
      '',
      'Options:',
      '  --env <env>           Target environment (prod, dev, local)',
      '  --json                Output as JSON (list/linked-org only)',
      '  --api-url <url>       API URL override (takes precedence over --env)',
      '  --token <key>         API token override',
    ].join('\n')
  );
}

export async function runAccount(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printAccountHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      break;
    case 'set':
      await handleSet(rest, apiUrl, token);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      break;
    case 'link-org':
      await handleLinkOrg(rest, apiUrl, token);
      break;
    case 'unlink-org':
      await handleUnlinkOrg(rest, apiUrl, token);
      break;
    case 'linked-org':
      await handleLinkedOrg(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printAccountHelp();
      process.exit(1);
  }
}
